"use client";

import { Check, Copy, Download } from "lucide-react";
import { useTranslations } from "next-intl";
import type { BundledLanguage } from "shiki";

import {
  Artifact,
  ArtifactAction,
  ArtifactActions,
  ArtifactDescription,
  ArtifactHeader,
  ArtifactTitle,
} from "@/components/ai-elements/artifact";
import { CodeBlock } from "@/components/ai-elements/code-block";
import {
  JSXPreview,
  JSXPreviewContent,
  JSXPreviewError,
} from "@/components/ai-elements/jsx-preview";
import {
  SandboxTabContent,
  SandboxTabs,
  SandboxTabsBar,
  SandboxTabsList,
  SandboxTabsTrigger,
} from "@/components/ai-elements/sandbox";
import {
  WebPreview,
  WebPreviewBody,
} from "@/components/ai-elements/web-preview";
import { CopyButton } from "@/components/ui/copy-button";

type ArtifactKind = "html" | "svg" | "jsx";

type ChatArtifact = {
  kind: ArtifactKind;
  language: BundledLanguage;
  code: string;
};

const DOWNLOAD_EXTENSIONS: Record<ArtifactKind, string> = {
  html: "html",
  svg: "svg",
  jsx: "jsx",
};

const FENCE = /```([\w-]*)[^\n]*\n([\s\S]*?)\n```/g;

function findArtifacts(text: string): ChatArtifact[] {
  const found: ChatArtifact[] = [];

  for (const match of text.matchAll(FENCE)) {
    const language = match[1].toLowerCase();
    const code = match[2].trim();

    if (!code) continue;

    if (language === "html" || language === "htm") {
      found.push({ kind: "html", language: "html", code });
    } else if (
      language === "svg" ||
      (language === "xml" && code.startsWith("<svg"))
    ) {
      found.push({ kind: "svg", language: "xml", code });
    } else if ((language === "jsx" || language === "tsx") && code.startsWith("<")) {
      found.push({ kind: "jsx", language: "jsx", code });
    }
  }

  return found;
}

export function ChatArtifacts({ text }: { text: string }) {
  const artifacts = findArtifacts(text);

  if (artifacts.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      {artifacts.map((artifact, index) => (
        <ArtifactCard key={index} artifact={artifact} />
      ))}
    </div>
  );
}

function ArtifactCard({ artifact }: { artifact: ChatArtifact }) {
  const t = useTranslations("member.aiChat.artifact");
  const lines = artifact.code.split("\n").length;

  function download() {
    const url = URL.createObjectURL(
      new Blob([artifact.code], { type: "text/plain;charset=utf-8" }),
    );
    const link = document.createElement("a");

    link.href = url;
    link.download = `preview.${DOWNLOAD_EXTENSIONS[artifact.kind]}`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Artifact className="not-prose">
      <ArtifactHeader className="px-3 py-2">
        <div className="min-w-0">
          <ArtifactTitle>{t("title", { kind: t(`kind.${artifact.kind}`) })}</ArtifactTitle>
          <ArtifactDescription className="text-xs">{t("lines", { count: lines })}</ArtifactDescription>
        </div>
        <ArtifactActions>
          <CopyButton
            value={artifact.code}
            label={t("copyCode")}
            copiedLabel={t("copied")}
            successText={t("copied")}
            errorText={t("copyFailed")}
            render={(props, { copied }) => <ArtifactAction {...props} tooltip={copied ? t("copied") : t("copyCode")} icon={copied ? Check : Copy} />}
          />
          <ArtifactAction tooltip={t("download")} icon={Download} onClick={download} />
        </ArtifactActions>
      </ArtifactHeader>

      <SandboxTabs defaultValue="preview">
        <SandboxTabsBar className="border-t-0">
          <SandboxTabsList>
            <SandboxTabsTrigger value="preview">{t("preview")}</SandboxTabsTrigger>
            <SandboxTabsTrigger value="code">{t("code")}</SandboxTabsTrigger>
          </SandboxTabsList>
        </SandboxTabsBar>

        <SandboxTabContent value="preview">
          {artifact.kind === "jsx" ? (
            <JSXPreview jsx={artifact.code} className="p-4">
              <JSXPreviewContent />
              <JSXPreviewError />
            </JSXPreview>
          ) : (
            <WebPreview className="h-80 rounded-none border-0">
              <WebPreviewBody srcDoc={artifact.code} sandbox="allow-scripts" />
            </WebPreview>
          )}
        </SandboxTabContent>

        <SandboxTabContent value="code">
          <CodeBlock
            code={artifact.code}
            language={artifact.language}
            className="max-h-96 overflow-auto rounded-none border-0"
          />
        </SandboxTabContent>
      </SandboxTabs>
    </Artifact>
  );
}
